import type { DynamicPortfolio } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useUpdateDynamicPortfolio } from "@/lib/hooks/use-portfolio-collections";
import { useT } from "@/lib/i18n/use-t";

import { PreferenceMaximumFields } from "../preference-maximum-fields";
import { readPreferenceMaximums } from "../preference-maximums";
import { T } from "./preference-tilt-settings.i18n";

type PreferenceTiltSettingsProps = {
  collectionId: string;
  portfolio: Pick<DynamicPortfolio, "id" | "maxPerPreference" | "maxTotalPreferences">;
};

/**
 * The two maximums of a dynamic portfolio's preference tilt. Shown only when
 * the collection has client preferences.
 */
export function PreferenceTiltSettings({ collectionId, portfolio }: PreferenceTiltSettingsProps) {
  const t = useT(T);

  const updateDynamicPortfolio = useUpdateDynamicPortfolio();
  const [perPreference, setPerPreference] = useState(String(portfolio.maxPerPreference));
  const [total, setTotal] = useState(String(portfolio.maxTotalPreferences));

  useEffect(() => {
    setPerPreference(String(portfolio.maxPerPreference));
    setTotal(String(portfolio.maxTotalPreferences));
  }, [portfolio.maxPerPreference, portfolio.maxTotalPreferences]);

  const maximums = readPreferenceMaximums(perPreference, total);
  const isDirty =
    maximums.error === null &&
    (maximums.maxPerPreference !== portfolio.maxPerPreference ||
      maximums.maxTotalPreferences !== portfolio.maxTotalPreferences);

  const handleSave = () => {
    if (maximums.error !== null) return;
    const { maxPerPreference, maxTotalPreferences } = maximums;
    updateDynamicPortfolio.mutate(
      {
        collectionId,
        dynamicPortfolioId: portfolio.id,
        data: { maxPerPreference, maxTotalPreferences },
      },
      {
        onSuccess: () => toast.success(t.saved),
        onError: (err: Error) => toast.error(err.message || t.failedToSave),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.title}</CardTitle>
        <CardDescription>{t.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <PreferenceMaximumFields
          idPrefix="preference-tilt"
          perPreference={perPreference}
          total={total}
          onPerPreferenceChange={setPerPreference}
          onTotalChange={setTotal}
        >
          <Button
            onClick={handleSave}
            disabled={!isDirty}
            loading={updateDynamicPortfolio.isPending}
          >
            {t.save}
          </Button>
        </PreferenceMaximumFields>
      </CardContent>
    </Card>
  );
}
